import { useCallback, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit } from "../types/walletDebits.types";

export type WalletDebitUploadState =
  | { status: "idle" }
  | { status: "uploading"; count: number }
  | { status: "done"; count: number }
  | { status: "failed"; error: string };

/**
 * Uploads the proof of a manual send to its debit. Separate from the create on purpose: the money already
 * moved, so a failed upload never undoes the debit — it says so and the same files can be sent again.
 */
export function useWalletDebitAttachments(onUploaded: (debit: WalletDebit) => void) {
  const [state, setState] = useState<WalletDebitUploadState>({ status: "idle" });

  const upload = useCallback(
    async (debitId: string, files: File[]): Promise<boolean> => {
      if (!files.length) return true;
      setState({ status: "uploading", count: files.length });
      try {
        const debit = await WalletDebitsService.uploadAttachments(debitId, files);
        setState({ status: "done", count: files.length });
        onUploaded(debit);
        return true;
      } catch (error: unknown) {
        setState({ status: "failed", error: HttpApiService.getErrorMessage(error) });
        return false;
      }
    },
    [onUploaded],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, upload, reset };
}
